import type { PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import {
  applyPropertyAddressToSource,
  type PropertyAddressData,
} from 'src/helper/propertyAddressBroadcast';
import type { AssigneeDetails, ValueOf } from 'src/helper/shared-types';
import { sortData } from 'src/helper/utils';
import type { Issue } from 'src/redux/issues/types';
import {
  type DescriptionHistoryType,
  type InitialStateType,
  type RealtimeWorkOrderPayload,
  type ResidentAvailabilityResponseType,
  type ResidentInfoType,
  type ServiceRequestAPIResponseObj,
  type ServiceRequestDataObj,
  type ServiceRequestMetaDataObj,
  serviceRequestInitialState,
  type WorkOrderWithAdditionalData,
} from 'src/redux/service-request/types';
import type { NormalizedResidentAvailabilityType } from 'src/redux/work-order/types';
import {
  addToListOrReorderTheList,
  type NewItemCreationEvent,
  removeDataIdFromList,
  setGroupedDataMetaLoadingReducer,
  updateDataByGroupOptions,
} from '../common/reducers';

const getNormalizedAvailability = (availabilityArray: ResidentAvailabilityResponseType[]) => {
  if (!Array.isArray(availabilityArray)) return null;

  const source: NormalizedResidentAvailabilityType = {};
  const dates: string[] = [];

  availabilityArray.forEach((item) => {
    if (item.date) {
      source[item.date] = item;
      dates.push(item.date);
    }
  });

  return { source, dates };
};

const reducers = {
  addToSrListOrReorderTheList: (
    state: InitialStateType,
    action: PayloadAction<{
      id: string;
      isGroupBySelected: boolean;
      data: ServiceRequestAPIResponseObj;
      listKey: 'service_requests';
      pusherEvent: NewItemCreationEvent;
    }>,
  ) => {
    addToListOrReorderTheList(state, action);
  },

  removeIssue: (
    state: InitialStateType,
    action: PayloadAction<{
      issueId: string;
      serviceRequestId: string;
    }>,
  ) => {
    const { issueId, serviceRequestId } = action.payload;
    if (serviceRequestId && state?.source?.[serviceRequestId]) {
      state.source[serviceRequestId] = {
        ...state?.source?.[serviceRequestId],
        issues: state?.source?.[serviceRequestId]?.issues?.filter((id) => id !== issueId),
      };
    }
  },

  clearServiceRequestData: (state: InitialStateType) => {
    state.data = serviceRequestInitialState?.data;
    state.groupedData = serviceRequestInitialState?.groupedData;
    state.groupedDataMeta = {};
  },

  setGroupedDataMetaLoading: setGroupedDataMetaLoadingReducer,

  updateServiceRequestsByGroupOptions: (
    state: InitialStateType,
    action: PayloadAction<{
      response: { data: Array<ServiceRequestDataObj>; meta: ServiceRequestMetaDataObj };
      groupByOptionsList: Array<string>;
    }>,
  ) => {
    updateDataByGroupOptions(state, action, 'service_request_id');
  },

  updateServiceRequestAssigneeDetails: (
    state: InitialStateType,
    action: PayloadAction<{
      dataId: string;
      data: AssigneeDetails;
      key: string;
      event: string;
    }>,
  ) => {
    if (action?.payload?.data?.user_id && action?.payload?.dataId) {
      const currentData = { ...(state.source || {}) };

      let updatedServiceRequest = {} as ServiceRequestDataObj;
      if (action?.payload?.event === 'servicerequest.assignee.add') {
        const doesAssigneeExist = (state.source?.[action?.payload?.dataId]?.assignees || [])?.some(
          (item) => item?.user_id === action?.payload?.data?.user_id,
        );
        if (!doesAssigneeExist) {
          updatedServiceRequest = {
            ...(state.source[action?.payload?.dataId] || {}),
            [action?.payload?.key]: [
              ...(state.source?.[action?.payload?.dataId]?.assignees || []),
              action?.payload?.data,
            ],
          };
          state.source = {
            ...currentData,
            [action?.payload?.dataId]: updatedServiceRequest,
          };
        }
      }
      if (action?.payload?.event === 'servicerequest.assignee.delete') {
        updatedServiceRequest = {
          ...(state.source?.[action?.payload?.dataId] || {}),
          [action?.payload?.key]: [
            ...(state.source?.[action?.payload?.dataId]?.assignees || []),
          ]?.filter((item) => item?.user_id !== action?.payload?.data?.user_id),
        };
        state.source = {
          ...currentData,
          [action?.payload?.dataId]: updatedServiceRequest,
        };
      }
    }
  },

  removeSrIdFromList: (state: InitialStateType, action: PayloadAction<string>) => {
    removeDataIdFromList(state, action);
  },

  shouldSidePanel: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['showSidePanel']>,
  ) => {
    state.showSidePanel = action.payload;
  },

  updateServiceRequestSidePanelDetails: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['showSidePanel']>,
  ) => {
    if (state.showSidePanel) {
      state.showSidePanel = { ...(state.showSidePanel || {}), ...(action.payload || {}) };
    }
  },
  toggleToRefetchServiceRequestDetails: (
    state: InitialStateType,
    action: PayloadAction<boolean>,
  ) => {
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: action?.payload,
    };
  },

  resetSidePanelToActivityLog: (state: InitialStateType) => {
    const rightSection = state.showSidePanel?.rightSection || '';
    const mobileSection = state.showSidePanel?.mobileSection || '';
    if (!['activityLog']?.includes(rightSection) || !['activityLog']?.includes(mobileSection)) {
      state.showSidePanel = {
        ...(state.showSidePanel || {}),
        ...{
          rightSection: 'activityLog',
          mobileSection: 'activityLog',
        },
      };
    }
  },
  /**
   * Optimistic stamp written the moment the 202 lands. The queued job has not run
   * yet, so `requested_at` here is a client clock, not server truth — record what it
   * replaced so `resolveResidentAvailabilityRequest` can undo it if the send failed.
   */
  updateResidentAvailabilityRequestedAt: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      requested_at: string;
    }>,
  ) => {
    const { serviceRequestId, requested_at: requestedAt } = action.payload;
    const currentData = state.source[serviceRequestId];
    state.pendingAvailabilityRequests = {
      ...(state.pendingAvailabilityRequests || {}),
      [serviceRequestId]: {
        previousRequestedAt: currentData?.resident_availabilities?.requested_at || '',
      },
    };
    const updatedResidentAvailability = {
      ...(currentData?.resident_availabilities || {}),
      requested_at: requestedAt,
    };
    state.source[action?.payload?.serviceRequestId] = {
      ...currentData,
      resident_availabilities: updatedResidentAvailability,
    };
  },

  /**
   * Terminal outcome of a single-SR availability request, driven by the
   * `serviceRequest.requestAvailability.completed` broadcast.
   *
   * The broadcast is org-wide, so the pending-entry guard is what keeps one
   * coordinator's send from rewriting another's banner: only the client that wrote
   * the optimistic stamp settles it. `requestedAt` is omitted by the backend
   * whenever the server holds no timestamp (a failed or skipped send), in which
   * case the optimistic stamp is rolled back to what it replaced.
   */
  resolveResidentAvailabilityRequest: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      requestedAt?: string | null;
    }>,
  ) => {
    const { serviceRequestId, requestedAt } = action.payload;
    const pendingRequest = state.pendingAvailabilityRequests?.[serviceRequestId];
    if (!pendingRequest) return;

    delete state.pendingAvailabilityRequests[serviceRequestId];

    const currentData = state.source[serviceRequestId];
    if (!currentData) return;

    state.source[serviceRequestId] = {
      ...currentData,
      resident_availabilities: {
        ...(currentData?.resident_availabilities || {}),
        requested_at: requestedAt || pendingRequest.previousRequestedAt,
      },
    };
  },

  /**
   * Backstop for a request that never received its terminal broadcast (dropped
   * frame, socket reconnect). Refetching is safe here — unlike the eager refetch
   * this replaced, it fires long after the queued job has committed, so it can only
   * settle the banner on server truth, never race it (APX-607).
   */
  reconcileResidentAvailabilityRequest: (
    state: InitialStateType,
    action: PayloadAction<{ serviceRequestId: string }>,
  ) => {
    const { serviceRequestId } = action.payload;
    if (!state.pendingAvailabilityRequests?.[serviceRequestId]) return;

    delete state.pendingAvailabilityRequests[serviceRequestId];

    // The coordinator may have moved to another service request by now; only the
    // open panel can be refetched, and a closed panel refetches on reopen anyway.
    if (state.showSidePanel?.serviceRequestUuid !== serviceRequestId) return;

    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: true,
    };
  },
  updateResidentInfo: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId?: string;
      residents: Array<ResidentInfoType>;
      newDefaultResidentId: string;
    }>,
  ) => {
    const { serviceRequestId, residents, newDefaultResidentId } = action.payload;
    if (serviceRequestId) {
      const serviceRequest = { ...(state.source[serviceRequestId] || {}) };
      const updatedServiceRequest = {
        ...serviceRequest,
        resident_id: newDefaultResidentId,
        property: {
          ...(serviceRequest?.property || {}),
          residents,
        },
      };
      state.source[serviceRequestId] = updatedServiceRequest;
    }
  },
  updateSRAccessInfo: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId?: string;
      accessInformation: ServiceRequestDataObj['property']['access_information'];
    }>,
  ) => {
    const { serviceRequestId, accessInformation } = action.payload;
    if (serviceRequestId) {
      const serviceRequest = { ...(state.source[serviceRequestId] || {}) };
      const updatedServiceRequest = {
        ...serviceRequest,
        property: {
          ...(serviceRequest?.property || {}),
          access_information: accessInformation,
        },
      };
      state.source[serviceRequestId] = updatedServiceRequest;
    }
  },
  updateServiceRequestDetails: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      data: ValueOf<ServiceRequestDataObj>;
      key: keyof ServiceRequestDataObj;
    }>,
  ) => {
    const { key, data, serviceRequestId } = action.payload;
    const currentData = { ...(state.source || {}) };
    const existingServiceRequest = state.source[serviceRequestId] || {};
    let updateServiceRequest = {} as ServiceRequestDataObj;
    if (key === 'description_history') {
      const previous = state?.source?.[serviceRequestId]?.description_history || [];
      const normalizedPrevious = previous.map((item) =>
        item?.is_active ? { ...item, is_active: false } : item,
      );
      const incoming = Array.isArray(data) ? data : [data];
      updateServiceRequest = {
        ...existingServiceRequest,
        description_history: [...(incoming as DescriptionHistoryType[]), ...normalizedPrevious],
      };
    } else {
      updateServiceRequest = {
        ...existingServiceRequest,
        [key]: data,
      };
    }

    state.source = { ...currentData, [serviceRequestId]: updateServiceRequest };
  },

  updateSRWorkOrderStep: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['currentWorkOrderStep']>,
  ) => {
    state.currentWorkOrderStep = {
      ...(state.currentWorkOrderStep || {}),
      ...(action?.payload || {}),
    } as InitialStateType['currentWorkOrderStep'];
  },

  updateSubmittingWorkOrder: (
    state: InitialStateType,
    action: PayloadAction<{
      isSubmitting: boolean;
    }>,
  ) => {
    state.isSubmittingWorkOrder = action?.payload?.isSubmitting;
  },

  updateDeletingWorkOrder: (
    state: InitialStateType,
    action: PayloadAction<{
      isDeletingWorkOrder: boolean;
    }>,
  ) => {
    state.isDeletingWorkOrder = action?.payload?.isDeletingWorkOrder;
  },
  addIssues: (
    state: InitialStateType,
    action: PayloadAction<{
      issueId: string;
      serviceRequestId: string;
    }>,
  ) => {
    const { issueId, serviceRequestId } = action.payload;
    const currentIssues = state?.source?.[serviceRequestId]?.issues || [];
    if (!currentIssues?.includes(issueId)) {
      state.source[serviceRequestId] = {
        ...(state?.source?.[serviceRequestId] || {}),
        issues: [...currentIssues, issueId],
      };
    }
  },

  undoDeletedIssue: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      issueId: string;
      source: { [key: string]: Issue };
    }>,
  ) => {
    const { source, serviceRequestId, issueId } = action.payload;
    if (!isEmpty(source)) {
      const issueIds = [state?.source?.[serviceRequestId]?.issues || [], issueId].flat();
      const filteredSource = Object.entries(source)
        .filter(([key]) => issueIds.includes(key))
        .reduce((obj: { [key: string]: Issue }, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});

      const newIssueData = sortData(filteredSource, 'created_at', 'asc');

      state.source[serviceRequestId] = {
        ...(state?.source?.[serviceRequestId] || {}),
        issues: Object.keys(newIssueData),
      };
    }
  },
  /**
   * Insert-or-merge a work order that arrived over Pusher into a service request's
   * `work_orders` list.
   *
   * Replaces `appendNewDataInServiceRequestData`, which prepended the realtime frame
   * VERBATIM with no defaults. The `serviceRequest.workOrder.created` payload carries
   * no `issues` key at all, so that left `work_orders[i].issues` `undefined` and the
   * work order card rendered zero issue bullets (ORI-571).
   *
   * - not found -> PREPEND, defaulting `issues` to `[]` so the key is never `undefined`;
   * - found -> merge in place, PRESERVING list position.
   *
   * `issues` follows the same nullish rule as every other key in the spread, stated
   * explicitly because the fix depends on it: only an EXPLICIT `[]` clears. An absent,
   * `undefined` or `null` `issues` falls through to whatever the SR-details GET already
   * seeded, so a redelivered created frame (which never carries the key) cannot reduce
   * a card's issues; a payload that DOES carry issues is authoritative, so this reducer
   * will not discard a populated array in favour of an empty stored one.
   */
  upsertWorkOrderInServiceRequest: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      workOrder: RealtimeWorkOrderPayload;
    }>,
  ) => {
    const { serviceRequestId, workOrder } = action.payload;
    if (!serviceRequestId || !workOrder?.work_order_id) return;

    const currentWorkOrders = state?.source?.[serviceRequestId]?.work_orders || [];
    const existingIndex = currentWorkOrders.findIndex(
      (item) => item?.work_order_id === workOrder?.work_order_id,
    );

    state.source[serviceRequestId] = {
      ...(state?.source?.[serviceRequestId] || {}),
      work_orders:
        existingIndex === -1
          ? [
              { ...workOrder, issues: workOrder?.issues ?? [] } as WorkOrderWithAdditionalData,
              ...currentWorkOrders,
            ]
          : currentWorkOrders.map((existingWorkOrder) => {
              if (existingWorkOrder?.work_order_id !== workOrder?.work_order_id) {
                return existingWorkOrder;
              }
              return {
                ...existingWorkOrder,
                ...workOrder,
                issues: workOrder?.issues ?? existingWorkOrder?.issues ?? [],
              };
            }),
    };
  },
  updateIssueInServiceRequestWorkOrders: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      workOrderId?: string;
      title?: string;
      issueId: string;
      type: 'add' | 'delete' | 'unAssign';
      assignedWorkOrderIds?: string[];
    }>,
  ) => {
    const { serviceRequestId, workOrderId, title, issueId, type, assignedWorkOrderIds } =
      action.payload;
    if (serviceRequestId) {
      // add issue to work orders in service request
      if (type === 'add' && workOrderId && title) {
        state.source[serviceRequestId] = {
          ...(state?.source?.[serviceRequestId] || {}),
          work_orders: (state?.source?.[serviceRequestId]?.work_orders || []).map((workOrder) => {
            if (workOrder?.work_order_id === workOrderId) {
              return {
                ...workOrder,
                issues: [...(workOrder?.issues || []), { title, issue_id: issueId }],
              };
            }
            return workOrder;
          }),
        };
      }
      // Delete issue from work order and remove work order
      // If it's the last issue we remove the work order
      if (type === 'delete') {
        const assignedWoIds = assignedWorkOrderIds || [];
        state.source[serviceRequestId] = {
          ...(state?.source?.[serviceRequestId] || {}),
          work_orders: (state?.source?.[serviceRequestId]?.work_orders || [])
            .filter((workOrder) => {
              // Remove the work order only if the deleted issue was actually assigned to it
              // (its id is in the issue's own work_orders list) AND it was the WO's last issue.
              // An unassigned issue carries no assigned WO ids, so no work order is removed.
              if (
                assignedWoIds.includes(workOrder?.work_order_id) &&
                workOrder?.issues?.length === 1
              ) {
                return false;
              }
              return true;
            })
            .map((workOrder) => ({
              ...workOrder,
              issues: workOrder?.issues?.filter((issue) => issue.issue_id !== issueId),
            })),
        };
      }
      // Remove the issue ID from the work order matching the provided workOrderId.
      //  If the matching work order contains only one issue, remove the entire work order.
      if (type === 'unAssign') {
        // Whether the targeted work order actually LISTED the un-assigned issue before
        // the map below removed it. Removal must be PROVEN, not inferred from an empty
        // array: a card seeded by the realtime `serviceRequest.workOrder.created` frame
        // holds `issues: []` until the SR-details refetch lands, and un-assigning some
        // other issue must not delete it (ORI-571).
        const targetedWorkOrderHadIssue = !!(state?.source?.[serviceRequestId]?.work_orders || [])
          .find((workOrder) => workOrder?.work_order_id === workOrderId)
          ?.issues?.some((issue) => issue?.issue_id === issueId);
        state.source[serviceRequestId] = {
          ...(state?.source?.[serviceRequestId] || {}),
          work_orders: (state?.source?.[serviceRequestId]?.work_orders || [])
            .map((workOrder) => {
              if (workOrder?.work_order_id === workOrderId) {
                return {
                  ...workOrder,
                  issues: [...(workOrder?.issues || [])].filter(
                    (issue) => issue.issue_id !== issueId,
                  ),
                };
              }
              return workOrder;
            })
            // Only the work order the issue was un-assigned FROM may be removed, and
            // only when we can prove we just removed its last KNOWN issue. An UNTARGETED
            // card MUST survive even when its own `issues` key is absent or empty - the
            // realtime `serviceRequest.workOrder.created` frame seeds cards with no issues
            // yet (ORI-571), and the previous unscoped `issues?.length > 0` filter
            // silently deleted every one of them. The TARGETED card likewise survives when
            // it never listed the issue, because its empty array proves nothing about the
            // server's state until the SR-details refetch resolves.
            ?.filter((workOrder) => {
              if (workOrder?.work_order_id !== workOrderId) {
                return true;
              }
              return !targetedWorkOrderHadIssue || (workOrder?.issues?.length ?? 0) > 0;
            }),
        };
      }
    }
  },

  // Remove issue from issuesToAssign if selected issue is deleted by another user
  checkAndRemoveIfItemIsPresentInIssuesToAssign: (
    state: InitialStateType,
    action: PayloadAction<{
      issueId: string;
    }>,
  ) => {
    const { issueId } = action.payload;
    const ifItemExistInIssuesToAssign = state?.showSidePanel?.issuesToAssign?.some(
      (issue) => issue === issueId,
    );
    if (ifItemExistInIssuesToAssign) {
      state.showSidePanel = {
        ...(state?.showSidePanel || {}),
        issuesToAssign: state?.showSidePanel?.issuesToAssign?.filter((issue) => issue !== issueId),
      };
    }
  },
  /**
   * Merge a `serviceRequest.workOrder.updated` payload into an existing work order.
   * The merge is authoritative PER KEY - whatever the payload sends wins, whatever it
   * omits is preserved - and is shared by 30+ producers, so the body must not change.
   *
   * Three producer shapes reach it (hence the `RealtimeWorkOrderPayload` annotation -
   * none of them sends a complete work order):
   *  - the base event: `work_order_id`, `status`, `abilities`, `work_order_issues`,
   *    `work_order_ai_summary` (no `issues`, so `issues` is preserved);
   *  - the issue-updated subclass and the issue-assigned subclass: `work_order_id`,
   *    `issues`, `abilities` (here `issues` IS authoritative, including `[]`).
   */
  updateWorkOrderInServiceRequest: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      workOrderId: string;
      updatedWorkOrder: RealtimeWorkOrderPayload;
    }>,
  ) => {
    const { serviceRequestId, workOrderId, updatedWorkOrder } = action.payload;
    if (serviceRequestId && workOrderId) {
      state.source[serviceRequestId] = {
        ...(state?.source?.[serviceRequestId] || {}),
        work_orders: (state?.source?.[serviceRequestId]?.work_orders || []).map((workOrder) => {
          if (workOrder?.work_order_id === workOrderId) {
            return {
              ...workOrder,
              ...updatedWorkOrder,
            };
          }
          return workOrder;
        }),
      };
    }
  },

  deleteWorkOrderInServiceRequest: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      workOrderId: string;
    }>,
  ) => {
    const { serviceRequestId, workOrderId } = action.payload;
    if (serviceRequestId && workOrderId) {
      state.source[serviceRequestId] = {
        ...(state?.source?.[serviceRequestId] || {}),
        work_orders: (state?.source?.[serviceRequestId]?.work_orders || []).filter(
          (workOrder) => workOrder?.work_order_id !== workOrderId,
        ),
      };
    }
  },
  updateServiceRequestDetailsPropertyAddress: (
    state: InitialStateType,
    action: PayloadAction<{ serviceRequestId?: string; data: PropertyAddressData }>,
  ) => {
    const { serviceRequestId, data } = action.payload;
    state.source = applyPropertyAddressToSource(state.source, data, serviceRequestId);
  },

  updateResidentAvailabilityDatesSr: (
    state: InitialStateType,
    action: PayloadAction<{ serviceRequestId: string; data: NormalizedResidentAvailabilityType }>,
  ) => {
    const { serviceRequestId, data } = action.payload;

    state.source[serviceRequestId].resident_availabilities.resident_availability_source =
      data || {};
    state.source[serviceRequestId].resident_availabilities.available_dates = Object.keys(
      data || {},
    );
  },

  // Zeroes the SR list row's resident-chat unread badge. Dispatched from the
  // SR Chat panel once its existing mark-as-read flow completes for the
  // resident-type conversation — mirrors the row-level reducers above
  // (state.source[id] = {...}) rather than state.data, since ListingComponent
  // rows resolve their fields through RenderColumnUtils' state.source lookup.
  clearServiceRequestUnreadMessagesCount: (
    state: InitialStateType,
    action: PayloadAction<{ serviceRequestId: string }>,
  ) => {
    const { serviceRequestId } = action.payload;
    if (serviceRequestId && state.source?.[serviceRequestId]) {
      state.source[serviceRequestId] = {
        ...state.source[serviceRequestId],
        unread_messages_count: 0,
      };
    }
  },

  updateResidentAvailability: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      residentAvailability: ResidentAvailabilityResponseType[];
    }>,
  ) => {
    const { serviceRequestId, residentAvailability } = action.payload;
    if (serviceRequestId && state.source?.[serviceRequestId]) {
      const currentSr = state.source[serviceRequestId];
      const normalized = getNormalizedAvailability(residentAvailability);

      if (normalized) {
        state.source[serviceRequestId] = {
          ...currentSr,
          resident_availabilities: {
            ...currentSr.resident_availabilities,
            availability: residentAvailability,
            resident_availability_source: normalized.source,
            available_dates: normalized.dates,
            // Blanking the stamp is right once the resident has answered, but this
            // frame can land while a send from this client is still in flight — and
            // the optimistic stamp is then the only value the banner has, so wiping
            // it would drop the banner entirely instead of settling it (APX-607).
            requested_at: state.pendingAvailabilityRequests?.[serviceRequestId]
              ? currentSr.resident_availabilities?.requested_at || ''
              : '',
          },
        };
      }
    }
  },
};

export default reducers;
